import { useEffect, useRef } from "react";
import type React from "react";

const FOCUSABLE_SELECTOR =
  "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), " +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal-dialog a11y shared by the home page's "Ny oppskrift" and "Filter"
 * sheets: initial focus on `initialFocusRef`, a Tab focus-trap inside
 * `panelRef`, Escape closes, and focus returns to `restoreFocusRef` (the
 * trigger — Safari doesn't focus a button on click, so `activeElement` alone
 * isn't reliable) or else to whatever was focused on open.
 *
 * Keys pressed inside a NESTED modal (the video/pdf import modals live inside
 * the sheet's React tree) are ignored: Escape there must not close the sheet
 * and unmount an import that is in flight.
 *
 * Mount/unmount-only on purpose (same reasoning as `ShoppingListOverlay`):
 * `onClose` is a fresh closure each render, and re-subscribing would re-run
 * the initial-focus step.
 */
export function useDialogA11y({
  panelRef,
  initialFocusRef,
  restoreFocusRef,
  onClose,
}: {
  panelRef: React.RefObject<HTMLElement | null>;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  restoreFocusRef?: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    (initialFocusRef?.current ?? panelRef.current)?.focus();

    function handleKeyDown(e: KeyboardEvent): void {
      const target = e.target as Element | null;
      if (target?.closest?.(".video-import-modal-backdrop")) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (!panel.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      (restoreFocusRef?.current ?? previouslyFocused)?.focus();
    };
  }, []);
}
